import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * אזורים בפלנוגרמה (מיגרציות 080, 090).
 *
 * אזור = איפה בחנות עומדת היחידה. "מה נמכר" היה פעם כאן כסוג
 * שני, והוא עבר למחלקות של הקטלוג: אותו מושג בדיוק, ושתי
 * רשימות נפרדות לאותו מושג הן שני מקומות להוסיף בהם.
 */
export type Zone = {
  id: string;
  kind: 'area';
  /**
   * הסניפים שבהם הוא קיים. ריק = כל הרשת (מיגרציה 082).
   *
   * מרתף יין יש בטאגור ובמגדלי תל אביב ולא בשאר, ולכן זו רשימה
   * ולא סניף אחד.
   */
  branch_ids: string[];
  name: string;
  color: string | null;
  is_active: boolean;
  sort_order: number;
};

const KEY = ['planogram-zones'] as const;

export function useZones() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<Zone[]> => {
      const { data, error } = await supabase.rpc('planogram_zone_list');
      if (error) throw error;
      return (data as unknown as Zone[]) ?? [];
    },
  });
}

/**
 * האזורים שחלים על סניף: הרשתיים ועוד שלו.
 *
 * מסונן בצד הלקוח ולא בשאילתה, כי הרשימה קטנה והיא ממילא
 * נטענת במלואה למסך הניהול.
 */
export function zonesFor(zones: Zone[], branchId: string | null): Zone[] {
  return zones.filter(
    (zone) =>
      zone.is_active &&
      (zone.branch_ids.length === 0 || (branchId !== null && zone.branch_ids.includes(branchId))),
  );
}

export function useZoneMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
    // הלוח מציג את השמות, ולכן הוא מתיישן יחד איתם
    void queryClient.invalidateQueries({ queryKey: ['planograms'] });
  };

  /**
   * השם וההיקף נשמרים יחד (מיגרציה 082).
   *
   * פעולה אחת ולא שתיים, כי שמירה שהצליחה חלקית היתה משאירה
   * אזור שקיים במקום הלא נכון.
   */
  const save = useMutation({
    mutationFn: async (input: {
      id?: string;
      branch_ids: string[];
      name: string;
      sort_order: number;
    }) => {
      const { error } = await supabase.rpc('save_planogram_zone', {
        p_kind: 'area',
        p_name: input.name.trim(),
        p_branches: input.branch_ids,
        p_id: input.id,
        p_sort_order: input.sort_order,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * כיבוי ולא מחיקה.
   *
   * אזור שנמחק משחרר את היחידות שבו (on delete set null), ואז
   * אי אפשר לדעת איפה הן היו. כיבוי מוציא אותו מהרשימות ומשאיר
   * את מה שכבר משויך אליו במקומו.
   */
  const setActive = useMutation({
    mutationFn: async (input: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('planogram_zones')
        .update({ is_active: input.is_active })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('planogram_zones').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { save, setActive, remove };
}
